import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import {
  ChatIcon,
  BriefcaseIcon,
  ShoppingCartIcon,
  AcademicCapIcon,
  GlobeAltIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/outline';

const useCases = [
  {
    name: 'HR AI Agent',
    description:
      'AI-powered agent to automate employee support, onboarding, and HR queries',
    href: '/chatbots/hr',
    icon: BriefcaseIcon,
    color: 'bg-blue-500',
  },
  {
    name: 'Customer Service AI Agent',
    description:
      'Intelligent AI agent providing 24/7 support and instant issue resolution',
    href: '/chatbots/customer-service',
    icon: ChatIcon,
    color: 'bg-green-500',
  },
  {
    name: 'Sales AI Agent',
    description:
      'Autonomous AI agent to qualify leads, book meetings, and boost conversions',
    href: '/chatbots/sales',
    icon: CurrencyDollarIcon,
    color: 'bg-yellow-500',
  },
  {
    name: 'Education AI Agent',
    description:
      'Smart AI agent supporting students with academic queries and admissions',
    href: '/chatbots/education',
    icon: AcademicCapIcon,
    color: 'bg-purple-500',
  },
  {
    name: 'Travel AI Agent',
    description:
      'AI travel agent for trip planning, bookings, and destination guidance',
    href: '/chatbots/travel',
    icon: GlobeAltIcon,
    color: 'bg-cyan-500',
  },
  {
    name: 'E-commerce AI Agent',
    description:
      'Shopping AI agent to guide customers and increase online sales',
    href: '/chatbots/ecommerce',
    icon: ShoppingCartIcon,
    color: 'bg-indigo-500',
  },
];

export default function UseCasesSection() {
  const { t } = useLocale('chatbots');

  return (
    <div className='bg-gray-50 dark:bg-slate-800 py-24 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto max-w-2xl text-center'>
          <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
            {t('AI Agent Solutions for Every Industry')}
          </h2>
          <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300'>
            {t('Explore how AI agents can transform your business operations')}
          </p>
        </div>
        <div className='mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-6 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-8'>
          {useCases.map((useCase) => (
            <Link
              key={useCase.name}
              href={useCase.href}
              className='group relative rounded-3xl bg-white dark:bg-slate-800/80 p-8 shadow-sm ring-1 ring-gray-900/5 dark:ring-white/10 hover:-translate-y-1 hover:shadow-xl hover:ring-primary-500/50 dark:hover:ring-primary-500/50 transition-all duration-300'
            >
              <div
                className={`inline-flex rounded-2xl p-4 shadow-sm ring-1 ring-inset ring-black/10 group-hover:scale-110 transition-transform duration-300 ${useCase.color}`}
              >
                <useCase.icon
                  className='h-7 w-7 text-white'
                  aria-hidden='true'
                />
              </div>
              <h3 className='mt-6 text-xl font-bold leading-8 text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors'>
                {t(useCase.name)}
              </h3>
              <p className='mt-3 text-base leading-7 text-gray-600 dark:text-slate-300'>
                {t(useCase.description)}
              </p>
              <span className='absolute inset-0' aria-hidden='true' />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
